import { LocationAggregate } from '../models/locationaggregate.model';
import { QuoteWizardViewModel } from './quoteWizard.viewmodel';
import { QuoteWizardGateway } from './quoteWizard.gateway';
import { IClientStateContext } from './clientstatecontext.model';
import { QuoteWizardNavigationService } from './quoteWizardNavigation.service';
import { IWizardCurrentNodeChangedEventArgs, NavigationDirection } from '../core/services/wizardNavigationService/wizardNavigationService';
import { BrandGateway } from './quoteWizardSteps/gateways/brand.gateway';
import { DependentDataService } from '../core/services/dependentDataService/dependentData.service';
import { BusinessTypeGateway } from './quoteWizardSteps/gateways/businessType.gateway';
import { PolicyGateway } from './quoteWizardSteps/gateways/policy.gateway';
import { PhoneGateway } from './quoteWizardSteps/gateways/phone.gateway';
import { ViewLockService } from '../core/services/viewLockService/viewLock.service';
import { ISubmissionResponse } from '../models/submissionResponse.model';
import { ISubmission } from '../models/submission.model';
import { IPhone } from '../models/phone.model';
import { IAllLinesPolicySummary } from '../models/alllinespolicysummary.model';
import { Constants } from '../models/constants/marketplace.constants';
import { IQuoteWizardPageParams } from './quoteWizardPageParams';
import { ISession } from '../core/services/sessionService/session.service';
import { VerticalGateway } from './quoteWizardSteps/gateways/vertical.gateway';
import { IVertical } from '../models/models';
import { AuthenticationService } from '../authentication/authentication.service';
import { IUserProfile } from '../models/models';

export class QuoteWizardStateService {
    public viewModel: QuoteWizardViewModel;

    public static $inject = ['quoteWizardGateway', 'quoteWizardNavigationService', 'viewLockService', 'dependentDataService', '$rootScope', 'authenticationService', '$stateParams', '$q'];

    constructor(private quoteWizardGateway: QuoteWizardGateway,
                private quoteWizardNavigationService: QuoteWizardNavigationService,
                private viewLockService: ViewLockService,
                private dependentDataService: DependentDataService,
                private $rootScope: angular.IRootScopeService,
                private authenticationService: AuthenticationService,
                private $stateParams: IQuoteWizardPageParams,
                private $q: ng.IQService) {
       this.activate();
    }
    public activate(): void {
        const unsubscribe = this.quoteWizardNavigationService.subscribeOnWizardNodeChange(
            (event: ng.IAngularEvent, args: IWizardCurrentNodeChangedEventArgs) => {
                this.handleOnWizardNodeChange(args);
            });
        this.$rootScope.$on('$destroy', () => unsubscribe());
    }

    private handleOnWizardNodeChange(args: IWizardCurrentNodeChangedEventArgs) {
        this.saveState();
        if (args.direction !== NavigationDirection.Next) {
            return;
        }
        this.dependentDataService.checkAndLoadDependentData();
    }

    private initializeDependentDataConfiguration() {
        // load brands from brand table for the restaurant vertical via api, depends on user logged in and vertical loaded
        this.viewModel.brandsPromise = this.dependentDataService.addDependentData(
            () => { return this.authenticationService.isAuthenticated(); },
            ['vertical.gateway', 'brand.gateway', (verticalGateway: VerticalGateway, brandGateway: BrandGateway) => {
                return verticalGateway.loadVerticalByAlias(Constants.Verticals.Restaurant)
                    .then((vertical: IVertical) => {
                        return brandGateway.loadBrands(vertical.id)
                            .then(brands => brands
                                .filter((brand: any) => brand.alias != "415") // McDonald's has a special process for quoting, so keep it out of the brand list here
                            );
                    });
            }]
        );

        // load business types from code table via api, depends on user logged in
        this.viewModel.businessTypesPromise = this.dependentDataService.addDependentData(
            () => { return this.authenticationService.isAuthenticated(); },
            ['businessType.gateway', (businessTypeGateway: BusinessTypeGateway) => businessTypeGateway.loadBusinessTypes()]
        );

        // load phone number, depends on user logged in
        this.viewModel.phonePromise = this.dependentDataService.addDependentData(
            () => { return this.authenticationService.isAuthenticated(); },
            ['phone.gateway', (phoneGateway: PhoneGateway) => phoneGateway.loadPhone(this.viewModel.clientStateContext.userId)]
        ).then((phone: IPhone) =>
            this.viewModel.phone = phone);

        // load applicable policies by brand via api, depends on brand and effective date populated
        this.dependentDataService.addDependentData(
            () => {
                return !!(this.viewModel.clientStateContext.submissionRequest.brandId &&
                    this.viewModel.clientStateContext.submissionRequest.policyEffectiveDate);
            },
            ['policy.gateway', (policyGateway: PolicyGateway) =>
                policyGateway.loadApplicablePoliciesByBrand(
                    this.viewModel.clientStateContext.submissionRequest.brandId,
                    this.viewModel.clientStateContext.submissionRequest.policyEffectiveDate)]
        ).then((applicablePolicies: number[]) => {
            this.viewModel.applicablePolicies = applicablePolicies;
        });

        // load the finished submission after saving the request, but only if it's a quote, not a referral
        this.viewModel.loadFinishedSubmissionPromise = this.dependentDataService.addDependentData(
            () => { return !!this.viewModel.finishedSubmissionResponse; },
            [() => this.quoteWizardGateway.getSubmissionById(this.viewModel.finishedSubmissionResponse.submissionId).then((submission: ISubmission) =>
                this.viewModel.finishedSubmission = submission)]
        );
        // as well as the policy summary info, but only if it's a quote, not a referral
        this.viewModel.loadPolicySummaryPromise = this.dependentDataService.addDependentData(
            () => angular.isDefined(this.viewModel.finishedSubmissionResponse) && this.viewModel.finishedSubmissionResponse.submissionStatus === Constants.SubmissionStatus.Quoted,
            [() => this.quoteWizardGateway.getPolicySummaryBySubmissionId(this.viewModel.finishedSubmissionResponse.submissionId).then((policySummary: IAllLinesPolicySummary) =>
                this.viewModel.finishedPolicySummary = policySummary)]
        );
    }

    public showViewLockIfStillWaitingOnPromise(promise: ng.IPromise<any>) {
        let isFinished = false;
        promise.then(() => isFinished = true);
        if (isFinished !== false) {
            return;
        }
        this.viewLockService.broadcast(1);
        promise.then(() => this.viewLockService.broadcast(0));
    }

    public initialize(): ng.IPromise<any> {
        return this.getUserId().then((userId: string) => {
            return this.quoteWizardGateway.loadViewModel(userId).then((viewModel: QuoteWizardViewModel) => {
                this.viewModel = viewModel;
                this.initializeDependentDataConfiguration();
                // if they haven't added a location yet, add the first default one for them so it shows up in the nav
                if (this.viewModel.clientStateContext.submissionRequest.locations.length === 0) {
                    this.addAnotherLocation();
                }
                this.quoteWizardNavigationService.initialize();
                // make sure to load all dependencies that are ready to load with our current state and wait for them before showing the user the wizard
                return this.dependentDataService.checkAndLoadDependentData();
            });
        });
    }

    private getUserId(): ng.IPromise<string> {
        if (this.$stateParams.userId) {
            return this.$q.resolve(this.$stateParams.userId);
        }
        if (this.authenticationService.isAuthenticated()) {
            return this.quoteWizardGateway.getUserProfile().then((userProfile: IUserProfile) => {
                return userProfile.id;
            });
        }
        return this.$q.resolve(undefined);
    }

    public initializeForReturningUserAfterWizardCompletion() {
        this.viewModel = new QuoteWizardViewModel();
        this.viewModel.isReturningUserAfterWizardCompletion = true;
    }

    public saveState() {
        // if we haven't passed auth yet, or if we've crossed the finish line, don't attempt to save state
        if (!this.authenticationService.isAuthenticated() || this.viewModel.finishedSubmissionResponse) {
            return;
        }
        this.viewModel.clientStateContext.navigationContext.wizardSavedState = this.quoteWizardNavigationService.saveCurrentState();
        this.quoteWizardGateway.saveIncompleteSubmission(this.viewModel).then((newContext: IClientStateContext) => this.viewModel.clientStateContext.id = newContext.id);
    }

    public saveAndQuoteSubmissionRequest(): ng.IPromise<any> {
        this.broadcastQuoteRequested();
        this.broadcastMenuNavigationEnabledChanged(false);
        return this.quoteWizardGateway.saveAndQuoteSubmissionRequest(this.viewModel)
            .then((submissionResponse: ISubmissionResponse) => {
                // temporarily, we will fire and forget the finish line endpoint, so the user doesn't have to wait
                // because of this, the submissionResponse will not actually be populated when this promise finishes, so don't use it
                // this.viewModel.finishedSubmissionResponse = submissionResponse;

                // also, because the finish line has probably not finished yet, we don't wait to call the dependent data service to load the submission data
                // the refer page only uses the viewmodel data, so we will not load anything else when finished until the real quote page comes back
                // this will trigger the dependent data service to start the next couple of api calls, even without clicking the next button
                this.broadcastQuoteReceived();
                /*                return this.dependentDataService.checkAndLoadDependentData()
                                    .then(() => {
                                        this.broadcastQuoteReceived();
                                    });
                */
            });
    }

    public addAnotherLocation(): void {
        this.viewModel.clientStateContext.submissionRequest.locations.push(new LocationAggregate(this.getNewLocationName()));
    }

    private getNewLocationName() {
        return `Store ${this.viewModel.clientStateContext.submissionRequest.locations.length + 1}`;
    }

    public subscribeOnWizardQuoteRequested(listener: (event: ng.IAngularEvent, ...args: any[]) => any): () => void {
        return this.$rootScope.$on("quoteWizardState:quoteRequested", listener);
    }

    private broadcastQuoteRequested() {
        this.$rootScope.$broadcast("quoteWizardState:quoteRequested");
    }

    public subscribeOnWizardQuoteRecieved(listener: (event: ng.IAngularEvent, ...args: any[]) => any): () => void {
        return this.$rootScope.$on("quoteWizardState:quoteRecieved", listener);
    }

    private broadcastQuoteReceived() {
        this.$rootScope.$broadcast("quoteWizardState:quoteRecieved");
    }

    public subscribeOnMenuNavigationEnabledChanged(listener: (event: ng.IAngularEvent, isEnabled: boolean) => any): () => void {
        return this.$rootScope.$on("quoteWizardState:menuNavigationEnabledChanged", listener);
    }

    public broadcastMenuNavigationEnabledChanged(isEnabled: boolean) {
        this.$rootScope.$broadcast("quoteWizardState:menuNavigationEnabledChanged", isEnabled);
    }

    public subscribeOnMenuNavigationClick(listener: (event: ng.IAngularEvent, isClicked: boolean) => any): () => void {
        return this.$rootScope.$on("quoteWizardState:menuNavigationClicked", listener);
    }

    public broadcastMenuNavigationClick(isClicked: boolean) {
        this.$rootScope.$broadcast("quoteWizardState:menuNavigationClicked", isClicked);
    }
}